import React from 'react';
import { Phone, MapPin, Mail, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <footer className="bg-[#0C0D0D] text-gray-400">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Brand & About */}
          <div>
            <div className="flex items-center mb-4">
              <img
                src={'/assets/logo-dark.svg'}
                alt="3D Barbers Logo"
                className="h-[55px] w-auto mr-2 transition-all duration-300"
              />
            </div>
            <p className="text-gray-300 mb-3">
                Love your look?<br />
                Leave us a review — it helps us rank higher on Google and makes it easier for new clients to discover us.
              </p>
              <a 
                href="https://www.google.com/search?q=3d+barbers+uxbridge+road&oq=3d+barbers+uxbridge+road&gs_lcrp=EgZjaHJvbWUqDAgAECMYJxiABBiKBTIMCAAQIxgnGIAEGIoFMhAIARAuGK8BGMcBGIAEGI4FMgYIAhBFGEAyBwgDEAAY7wUyCggEEAAYgAQYogQyCggFEAAYgAQYogQyCggGEAAYgAQYogTSAQgyOTM1ajBqN6gCB7ACAfEFeSPxz9nK8L7xBXkj8c_ZyvC-&sourceid=chrome&ie=UTF-8#lrd=0x48760fd22565ab43:0x2f9efafd581abfc2,1,,,," 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white hover:text-amber-500 transition-colors duration-300"
              >
                <img
                  src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_light_color_92x30dp.png"
                  alt="Google"
                  className="h-6 object-contain"
                />
                
                <span>Write a Review</span>
              </a>
            
            
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-white text-lg font-medium mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a href="#services" className="hover:text-amber-500 transition-colors duration-300">Our Services</a>
              </li>
              <li>
                <a href="#find-us" className="hover:text-amber-500 transition-colors duration-300">Find Us</a>
              </li>
              <li>
                <a href="#connect" className="hover:text-amber-500 transition-colors duration-300">Connect With Us</a>
              </li>
              <li>
                <a href="/about" className="hover:text-amber-500 transition-colors duration-300">About Us</a>
              </li>
            </ul>
          </div>
          
          {/* Book Services */}
          <div>
            <h4 className="text-white text-lg font-medium mb-4">Book Now</h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="https://cal.com/3dsalon/haircut" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Men's Haircut
                </a>
              </li>
              <li>
                <a 
                  href="https://cal.com/3dsalon/mens-long-style" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Men's Long Haircut
                </a>
              </li>
              <li>
                <a 
                  href="https://cal.com/3dsalon/short-styling" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Women's Styling
                </a>
              </li>
              <li>
                <a 
                  href="https://cal.com/3dsalon/styling" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Ladies' Braids
                </a>
              </li>
              <li>
                <a 
                  href="https://cal.com/3dsalon/short-styling" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Ladies' Styling
                </a>
              </li>
              <li>
                <a 
                  href="https://cal.com/3dsalon/kids" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-500 transition-colors duration-300"
                >
                  Kids
                </a>
              </li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="text-white text-lg font-medium mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 text-amber-500 mr-2 mt-0.5" />
                <span itemProp="address">272 Uxbridge Road<br />Shepherd's Bush, London W12 7JA</span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 text-amber-500 mr-2" />
                <span itemProp="telephone">+44 (20) 8743 6788</span>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 text-amber-500 mr-2" />
                <span itemProp="email">bookings3d@outlook.com</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8 text-center">
          <div className="flex items-center justify-center gap-4">
            <p>&copy; {currentYear} 3D Afro & Euro Unisex Salon. All rights reserved.</p>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-[#0C0D0D]"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-amber-500" />
              ) : (
                <Moon className="w-5 h-5 text-amber-500" />
              )}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;